import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { ChevronLeftIcon, DownloadIcon, TrendingUpIcon, FilterIcon, CheckCircle2Icon, CalendarIcon, XIcon } from 'lucide-react';
interface ReportsProps {
  onBack?: () => void;
}
export function Reports({
  onBack
}: ReportsProps) {
  const {
    colors
  } = useTheme();
  const [timePeriod, setTimePeriod] = useState<'week' | 'month' | 'year'>('month');
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filters, setFilters] = useState({
    categories: [] as string[],
    minScore: 0,
    maxScore: 100
  });
  const summaryStats = {
    overallCompliance: 97,
    completed: 28,
    avgTime: 4.2,
    pending: 2
  };
  const allSubmissions = [{
    id: '1',
    title: 'Pre-Patient Contact',
    category: 'Hand Hygiene',
    timestamp: '2024-01-15 14:30',
    duration: 2.5,
    score: 100
  }, {
    id: '2',
    title: 'Surgical Site Preparation',
    category: 'Surgical',
    timestamp: '2024-01-15 11:20',
    duration: 8.2,
    score: 95
  }, {
    id: '3',
    title: 'Medication Administration',
    category: 'Medical',
    timestamp: '2024-01-14 16:45',
    duration: 3.1,
    score: 100
  }, {
    id: '4',
    title: 'Patient Admission',
    category: 'General',
    timestamp: '2024-01-14 09:15',
    duration: 5.8,
    score: 92
  }, {
    id: '5',
    title: 'Wound Care Protocol',
    category: 'Surgical',
    timestamp: '2024-01-13 13:30',
    duration: 6.5,
    score: 98
  }, {
    id: '6',
    title: 'Hand Hygiene Check',
    category: 'Hand Hygiene',
    timestamp: '2024-01-13 10:00',
    duration: 1.5,
    score: 88
  }];
  const categories = ['Hand Hygiene', 'Surgical', 'Medical', 'General'];
  const filteredSubmissions = allSubmissions.filter(submission => {
    if (filters.categories.length > 0 && !filters.categories.includes(submission.category)) {
      return false;
    }
    if (submission.score < filters.minScore || submission.score > filters.maxScore) {
      return false;
    }
    return true;
  });
  const getScoreColor = (score: number) => {
    if (score >= 95) return colors.success;
    if (score >= 80) return colors.warning;
    return colors.error;
  };
  const toggleCategory = (category: string) => {
    setFilters(prev => ({
      ...prev,
      categories: prev.categories.includes(category) ? prev.categories.filter(c => c !== category) : [...prev.categories, category]
    }));
  };
  const clearFilters = () => {
    setFilters({
      categories: [],
      minScore: 0,
      maxScore: 100
    });
  };
  const hasActiveFilters = filters.categories.length > 0 || filters.minScore > 0 || filters.maxScore < 100;
  return <div className="min-h-screen -m-4" style={{
    backgroundColor: colors.background
  }} data-id="element-368">
      {/* Header */}
      <div className="sticky top-0 z-10 p-4 border-b" style={{
      backgroundColor: colors.cardSurface,
      borderColor: colors.divider
    }} data-id="element-369">
        <div className="flex items-center justify-between" data-id="element-370">
          <div className="flex items-center gap-4" data-id="element-371">
            {onBack && <button onClick={onBack} className="p-2 -ml-2" style={{
            color: colors.textPrimary
          }} data-id="element-372">
                <ChevronLeftIcon size={24} data-id="element-373" />
              </button>}
            <div data-id="element-374">
              <h1 className="text-xl font-bold" style={{
              color: colors.textPrimary
            }} data-id="element-375">
                Reports
              </h1>
              <p className="text-sm" style={{
              color: colors.textSecondary
            }} data-id="element-376">
                Your compliance history
              </p>
            </div>
          </div>
          <button className="p-2 rounded-lg" style={{
          color: colors.textPrimary
        }} data-id="element-377">
            <DownloadIcon size={24} data-id="element-378" />
          </button>
        </div>
      </div>
      <div className="p-4 space-y-6" data-id="element-379">
        {/* Time Period Selector */}
        <div className="p-6 rounded-xl" style={{
        backgroundColor: colors.cardSurface
      }} data-id="element-380">
          <div className="flex items-center gap-3 mb-4" data-id="element-381">
            <CalendarIcon size={24} style={{
            color: colors.textPrimary
          }} data-id="element-382" />
            <h2 className="text-lg font-bold" style={{
            color: colors.textPrimary
          }} data-id="element-383">
              Time Period
            </h2>
          </div>
          <div className="grid grid-cols-3 gap-3" data-id="element-384">
            {(['week', 'month', 'year'] as const).map(period => <button key={period} onClick={() => setTimePeriod(period)} className="p-3 rounded-lg font-medium transition-all" style={{
            backgroundColor: timePeriod === period ? colors.accent : 'transparent',
            color: timePeriod === period ? '#FFFFFF' : colors.textSecondary
          }} data-id="element-385">
                {period.charAt(0).toUpperCase() + period.slice(1)}
              </button>)}
          </div>
        </div>
        {/* Summary Statistics */}
        <div className="p-6 rounded-xl" style={{
        backgroundColor: colors.cardSurface
      }} data-id="element-386">
          <h2 className="text-lg font-bold mb-4" style={{
          color: colors.textPrimary
        }} data-id="element-387">
            Summary Statistics
          </h2>
          <div className="mb-4" data-id="element-388">
            <div className="flex items-center justify-between mb-2" data-id="element-389">
              <span style={{
              color: colors.textSecondary
            }} data-id="element-390">
                Overall Compliance
              </span>
              <div className="flex items-center gap-2" data-id="element-391">
                <TrendingUpIcon size={16} style={{
                color: colors.success
              }} data-id="element-392" />
                <span className="text-xl font-bold" style={{
                color: colors.textPrimary
              }} data-id="element-393">
                  {summaryStats.overallCompliance}%
                </span>
              </div>
            </div>
            <div className="w-full h-2 rounded-full overflow-hidden" style={{
            backgroundColor: colors.divider
          }} data-id="element-394">
              <div className="h-full rounded-full transition-all" style={{
              width: `${summaryStats.overallCompliance}%`,
              backgroundColor: colors.accent
            }} data-id="element-395" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4 mt-6" data-id="element-396">
            <div className="text-center" data-id="element-397">
              <div className="text-3xl font-bold mb-1" style={{
              color: colors.accent
            }} data-id="element-398">
                {summaryStats.completed}
              </div>
              <div className="text-sm" style={{
              color: colors.textSecondary
            }} data-id="element-399">
                Completed
              </div>
            </div>
            <div className="text-center" data-id="element-400">
              <div className="text-3xl font-bold mb-1" style={{
              color: colors.success
            }} data-id="element-401">
                {summaryStats.avgTime}
              </div>
              <div className="text-sm" style={{
              color: colors.textSecondary
            }} data-id="element-402">
                Avg Time (min)
              </div>
            </div>
            <div className="text-center" data-id="element-403">
              <div className="text-3xl font-bold mb-1" style={{
              color: colors.warning
            }} data-id="element-404">
                {summaryStats.pending}
              </div>
              <div className="text-sm" style={{
              color: colors.textSecondary
            }} data-id="element-405">
                Pending
              </div>
            </div>
          </div>
        </div>
        {/* Recent Submissions */}
        <div className="p-6 rounded-xl" style={{
        backgroundColor: colors.cardSurface
      }} data-id="element-406">
          <div className="flex items-center justify-between mb-4" data-id="element-407">
            <h2 className="text-lg font-bold" style={{
            color: colors.textPrimary
          }} data-id="element-408">
              Recent Submissions
            </h2>
            <button onClick={() => setShowFilterModal(true)} className="flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-lg transition-colors" style={{
            color: hasActiveFilters ? '#FFFFFF' : colors.accent,
            backgroundColor: hasActiveFilters ? colors.accent : colors.accent + '15'
          }} data-id="element-409">
              <FilterIcon size={16} data-id="element-410" />
              Filter
              {hasActiveFilters && <span className="w-2 h-2 rounded-full bg-white" data-id="element-411" />}
            </button>
          </div>
          <div className="space-y-3" data-id="element-412">
            {filteredSubmissions.map(submission => <div key={submission.id} className="p-4 rounded-lg border" style={{
            backgroundColor: colors.background,
            borderColor: colors.divider
          }} data-id="element-413">
                <div className="flex items-start gap-3" data-id="element-414">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{
                backgroundColor: colors.success + '20'
              }} data-id="element-415">
                    <CheckCircle2Icon size={20} style={{
                  color: colors.success
                }} data-id="element-416" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-417">
                    <h3 className="font-semibold mb-1" style={{
                  color: colors.textPrimary
                }} data-id="element-418">
                      {submission.title}
                    </h3>
                    <div className="text-sm mb-2" style={{
                  color: colors.textSecondary
                }} data-id="element-419">
                      {submission.category} • {submission.timestamp}
                    </div>
                    <div className="flex items-center gap-4 text-sm" data-id="element-420">
                      <span style={{
                    color: colors.textSecondary
                  }} data-id="element-421">
                        Duration:{' '}
                        <span className="font-medium" data-id="element-422">
                          {submission.duration} min
                        </span>
                      </span>
                      <span style={{
                    color: getScoreColor(submission.score)
                  }} data-id="element-423">
                        Score:{' '}
                        <span className="font-bold" data-id="element-424">{submission.score}%</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
        {/* Export Button */}
        <button className="w-full p-4 rounded-lg font-semibold flex items-center justify-center gap-2" style={{
        backgroundColor: colors.success,
        color: '#FFFFFF'
      }} data-id="element-425">
          <DownloadIcon size={20} data-id="element-426" />
          Export Full Report
        </button>
      </div>
      {/* Filter Modal */}
      {showFilterModal && <>
          <div className="fixed inset-0 z-40 bg-black bg-opacity-50" onClick={() => setShowFilterModal(false)} data-id="element-427" />
          <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-50 rounded-2xl max-w-md mx-auto" style={{
        backgroundColor: colors.cardSurface
      }} data-id="element-428">
            <div className="p-6" data-id="element-429">
              <div className="flex items-center justify-between mb-6" data-id="element-430">
                <h3 className="text-xl font-bold" style={{
              color: colors.textPrimary
            }} data-id="element-431">
                  Filter Submissions
                </h3>
                <button onClick={() => setShowFilterModal(false)} className="p-2 rounded-lg" style={{
              color: colors.textPrimary
            }} data-id="element-432">
                  <XIcon size={24} data-id="element-433" />
                </button>
              </div>
              {/* Categories */}
              <div className="mb-6" data-id="element-434">
                <h4 className="text-sm font-semibold mb-3" style={{
              color: colors.textPrimary
            }} data-id="element-435">
                  Categories
                </h4>
                <div className="flex flex-wrap gap-2" data-id="element-436">
                  {categories.map(category => <button key={category} onClick={() => toggleCategory(category)} className="px-4 py-2 rounded-lg text-sm font-medium transition-all" style={{
                backgroundColor: filters.categories.includes(category) ? colors.accent : colors.background,
                color: filters.categories.includes(category) ? '#FFFFFF' : colors.textPrimary,
                border: `1px solid ${filters.categories.includes(category) ? colors.accent : colors.divider}`
              }} data-id="element-437">
                      {category}
                    </button>)}
                </div>
              </div>
              {/* Score Range */}
              <div className="mb-6" data-id="element-438">
                <h4 className="text-sm font-semibold mb-3" style={{
              color: colors.textPrimary
            }} data-id="element-439">
                  Score Range
                </h4>
                <div className="space-y-4" data-id="element-440">
                  <div data-id="element-441">
                    <label className="text-sm mb-2 block" style={{
                  color: colors.textSecondary
                }} data-id="element-442">
                      Minimum: {filters.minScore}%
                    </label>
                    <input type="range" min="0" max="100" value={filters.minScore} onChange={e => setFilters(prev => ({
                  ...prev,
                  minScore: Number(e.target.value)
                }))} className="w-full" style={{
                  accentColor: colors.accent
                }} data-id="element-443" />
                  </div>
                  <div data-id="element-444">
                    <label className="text-sm mb-2 block" style={{
                  color: colors.textSecondary
                }} data-id="element-445">
                      Maximum: {filters.maxScore}%
                    </label>
                    <input type="range" min="0" max="100" value={filters.maxScore} onChange={e => setFilters(prev => ({
                  ...prev,
                  maxScore: Number(e.target.value)
                }))} className="w-full" style={{
                  accentColor: colors.accent
                }} data-id="element-446" />
                  </div>
                </div>
              </div>
              {/* Actions */}
              <div className="flex gap-3" data-id="element-447">
                <button onClick={clearFilters} className="flex-1 p-3 rounded-lg font-medium transition-colors" style={{
              color: colors.textPrimary,
              border: `1px solid ${colors.divider}`
            }} data-id="element-448">
                  Clear All
                </button>
                <button onClick={() => setShowFilterModal(false)} className="flex-1 p-3 rounded-lg font-medium" style={{
              backgroundColor: colors.accent,
              color: '#FFFFFF'
            }} data-id="element-449">
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        </>}
    </div>;
}